import React from "react";
import Link from "gatsby-link";
import styled, { css, ThemeProvider } from "styled-components";
import { rgba, shade, tint } from "polished";
import { Box, Flex, Text as RebassText } from "rebass";
import {
  H4,
  Logo,
  PrimaryButton,
  HamburgerMenuIcon,
  SearchIcon,
  Search,
  Select,
  Text,
  DropdownMenu,
} from "components";
import theme from "../theme";
import invertTheme from "../invertTheme";
import * as Icon from "react-feather";

const Wrapper = styled(Box)`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 9999;
  width: 100%;
  background: #fff;
`;
Wrapper.defaultProps = {
  is: "header",
};

const Container = styled(Box)`
  position: relative;
  max-width: 90em;
`;
Container.defaultProps = {
  is: "section",
  w: "100%",
  mx: "auto",
  px: [0, 5, 6, 8],
};

const HeaderContainer = styled(Flex)`
  position: relative;
  height: 80px;
`;
HeaderContainer.defaultProps = {
  // pr: [0, 4],
  // bg: "grey.2"
};

const LogoPanel = styled(Flex)`
  height: 80px;
  user-select: none;

  @media only screen and (max-width: 40rem) {
    &:not(:lang(ar)) {
      margin-left: 24px;
    }
    &:lang(ar) {
      margin-right: 24px;
    }
  }
`;
LogoPanel.defaultProps = {
  is: Link,
  title: "Bank Information Center",
  align: "center",
  py: 20,
};

const LogoContainer = styled(Box)`
  height: 40px;
  z-index: ${(props) => (props.open ? "9999" : "")};
`;

const LogoTitle = styled(H4)`
  color: ${(props) => props.theme.colors.violet} !important;
  height: 40px;
  &:not(:lang(ar)) {
    margin-left: 16px;
  }
  &:lang(ar) {
    margin-right: 16px;
  }
  color: #fff;

  @media (max-width: 30em) {
    display: none;
  }
  @media (min-width: 30em) and (max-width: 72em) {
    display: block;
  }
  @media (min-width: 72em) and (max-width: 85em) {
    display: none;
  }
`;
LogoTitle.defaultProps = {
  is: "span",
  lineHeight: 2,
};

const NavContainer = styled(Flex)`
  position: relative;
  width: 100%;

  @media (max-width: 40em) {
    position: absolute;
    top: 0;
    bottom: 0;
  }
`;

const NavItem = styled(RebassText)`
  cursor: pointer;
  transition: color 500ms cubic-bezier(0.23, 1, 0.32, 1);
  will-change: color;
  user-select: none;

  &:hover {
    color: ${(props) => props.theme.colors.violet};
  }
  &:active {
    color: ${(props) =>
      props.theme.colors.text && shade(0.875, props.theme.colors.violet)};
  }
`;
NavItem.defaultProps = {
  is: "span",
  m: 0,
  px: 2,
  py: 3,
  color: "text",
  fontSize: 16,
  fontWeight: "bold",
  lineHeight: 3,
};

const NavigationContainer = styled(Flex)`
  @media (max-width: 72em) {
    display: none;
  }
`;
NavigationContainer.defaultProps = {
  is: "nav",
  flex: 1,
  justify: "space-between",
  px: 24,
};

const NavSkip = styled(PrimaryButton)`
  position: fixed;
  top: 24px;
  left: -2000px;
  z-index: 9999;
  &:focus {
    left: 24px;
  }
`;

const Navigation = (props) => (
  <NavigationContainer>
    <DropdownMenu
      parent={
        props.windowLang === "en-us"
          ? "About"
          : props.windowLang === "es-mx"
          ? "Sobre BIC"
          : "عن نحن"
      }
      parentTo={"/" + props.windowLang + "/" + "about"}
      items={[
        {
          node: {
            lang: props.windowLang,
            uid: "team",
            data: {
              title: [
                {
                  text:
                    props.windowLang === "en-us"
                      ? "Team"
                      : props.windowLang === "es-mx"
                      ? "Equipo"
                      : "الفريق",
                },
              ],
            },
          },
        },
        {
          node: {
            lang: props.windowLang,
            uid: "contact",
            data: {
              title: [
                {
                  text:
                    props.windowLang === "en-us"
                      ? "Contact"
                      : props.windowLang === "es-mx"
                      ? "Contacto"
                      : "اتصل",
                },
              ],
            },
          },
        },
        {
          node: {
            lang: props.windowLang,
            uid: "support",
            data: {
              title: [
                {
                  text:
                    props.windowLang === "en-us"
                      ? "Support"
                      : props.windowLang === "es-mx"
                      ? "Súmate"
                      : "الدعم",
                },
              ],
            },
          },
        },
        {
          node: {
            lang: props.windowLang,
            uid: "careers",
            data: {
              title: [
                {
                  text:
                    props.windowLang === "en-us"
                      ? "Careers"
                      : props.windowLang === "es-mx"
                      ? "Empleo"
                      : "وظائف",
                },
              ],
            },
          },
        },
        {
          node: {
            lang: props.windowLang,
            uid: "reports",
            data: {
              title: [
                {
                  text:
                    props.windowLang === "en-us"
                      ? "Reports"
                      : props.windowLang === "es-mx"
                      ? "Informes"
                      : "التقارير",
                },
              ],
            },
          },
        },
      ]}
      linkType=""
      lang={props.windowLang}
    />
    <DropdownMenu
      parent={
        props.windowLang === "en-us"
          ? "Campaigns"
          : props.windowLang === "es-mx"
          ? "Campañas"
          : "حملات"
      }
      parentTo={"/" + props.windowLang + "/" + "campaigns"}
      items={[
        ...props.data.landing_pages.edges.filter((campaign) =>
          campaign.node.data.show_in_navigation === "Show in navigation"
            ? true
            : false
        ),
        ...props.data.campaigns.edges.filter((campaign) =>
          campaign.node.data.show_in_navigation !== "No" ? true : false
        ),
      ]}
      // linkType="campaign"
      lang={props.windowLang}
    />
    <DropdownMenu
      parent={
        props.windowLang === "en-us"
          ? "Regions"
          : props.windowLang === "es-mx"
          ? "Regiones"
          : "مناطق"
      }
      parentTo={"/" + props.windowLang + "/" + "regions"}
      items={props.data.regions.edges}
      linkType="region"
      lang={props.windowLang}
    />
    <DropdownMenu
      parent={
        props.windowLang === "en-us"
          ? "Institutions"
          : props.windowLang === "es-mx"
          ? "Instituciones"
          : "مؤسسات"
      }
      parentTo={"/" + props.windowLang + "/" + "institutions"}
      items={props.data.institutions.edges}
      linkType="institution"
      lang={props.windowLang}
    />
    <NavItem
      children={
        props.windowLang === "en-us"
          ? "Resources"
          : props.windowLang === "es-mx"
          ? "Recursos"
          : " مصادر"
      }
      is={Link}
      to={"/" + props.windowLang + "/" + "resources"}
    />
    <NavItem
      children={
        props.windowLang === "en-us"
          ? "Updates"
          : props.windowLang === "es-mx"
          ? "Noticias"
          : "تحديثات"
      }
      is={Link}
      to={"/" + props.windowLang + "/" + "updates"}
    />
  </NavigationContainer>
);

const ButtonWrapper = styled(Box)`
  @media (min-width: 40em) and (max-width: 72em) {
    &:not(:lang(ar)) {
      margin-right: 32px;
    }
    &:lang(ar) {
      margin-left: 32px;
    }
  }
  @media (max-width: 45em) {
    display: none;
  }
`;

const SearchContainer = styled(Box)`
  position: relative;
  width: 44px;
  height: 44px;
  text-decoration: none;
  border: 0;
  border-radius: 9999px;
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  appearance: none;
  transition: background-color 500ms cubic-bezier(0.23, 1, 0.32, 1);

  svg > g > g {
    transition: stroke 500ms cubic-bezier(0.23, 1, 0.32, 1);
    will-change: stroke;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.violet};
    svg > g > g {
      stroke: #fff;
    }
  }
  &:focus {
    background-color: ${(props) => props.theme.colors.violet};
    box-shadow: 0 0 0 4px
      ${(props) =>
        props.theme.colors.text && rgba(props.theme.colors.violet, 0.5)};
    outline: none;
    svg > g > g {
      stroke: #fff;
    }
  }
  &:active {
    background-color: ${(props) =>
      props.theme.colors.text && shade(0.875, props.theme.colors.violet)};
    svg > g > g {
      stroke: #fff;
    }
  }
`;
SearchContainer.defaultProps = {
  is: "button",
  mx: 3,
};

const SearchIconContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 24px;
  height: 24px;
  transform: translate(-50%, -50%);
`;

const SearchButton = (props) => (
  <SearchContainer {...props}>
    <SearchIconContainer>
      <Icon.Search size={24} />
    </SearchIconContainer>
  </SearchContainer>
);

const LanguageDesktopWrapper = styled(Box)`
  @media (max-width: 72em) {
    display: none;
  }
`;

const LanguageTabletWrapper = styled(Box)`
  display: none;
  @media (min-width: 40em) and (max-width: 72em) {
    display: block;
  }
`;

const LanguageMobileWrapper = styled(Box)`
  border-bottom: 1px solid;
  border-color: ${(props) =>
    props.theme.colors.grey[1] && shade(0.9, props.theme.colors.grey[1])};
  @media (min-width: 40em) {
    display: none;
  }
`;

const HamburgerMenuContainer = styled(Box)`
  position: relative;
  width: 44px;
  height: 44px;
  text-decoration: none;
  border: 0;
  border-radius: 9999px;
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  appearance: none;
  transition: background-color 500ms cubic-bezier(0.23, 1, 0.32, 1);

  svg > g > g {
    transition: stroke 500ms cubic-bezier(0.23, 1, 0.32, 1);
    will-change: stroke;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.violet};
    svg > g > g {
      stroke: #fff;
    }
  }
  &:focus {
    background-color: ${(props) => props.theme.colors.violet};
    box-shadow: 0 0 0 4px
      ${(props) =>
        props.theme.colors.text && rgba(props.theme.colors.violet, 0.5)};
    outline: none;
    svg > g > g {
      stroke: #fff;
    }
  }
  &:active {
    background-color: ${(props) =>
      props.theme.colors.text && shade(0.875, props.theme.colors.violet)};
    svg > g > g {
      stroke: #fff;
    }
  }
  @media (min-width: 72em) {
    display: none;
  }
`;
HamburgerMenuContainer.defaultProps = {
  is: "button",
};

const HamburgerMenuIconContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 24px;
  height: 18px;
  transform: translate(-50%, -50%);
`;

const HamburgerMenu = (props) => (
  <HamburgerMenuContainer onClick={props.onClick}>
    <HamburgerMenuIconContainer>
      <HamburgerMenuIcon />
    </HamburgerMenuIconContainer>
  </HamburgerMenuContainer>
);

const MobileMenu = styled(Box)`
  box-shadow: 0 10px 16px 0 rgba(0, 0, 0, 0.2);
  @media (min-width: 72em) {
    display: none;
  }
`;
MobileMenu.defaultProps = {
  is: "nav",
  w: "100%",
  mx: "auto",
};

const MobileMenuItem = styled(Flex)`
  display: block;
  color: ${(props) => props.theme.colors.text};
  background: #fff;
  border-top: 1px solid;
  border-color: ${(props) => props.theme.colors.grey[1]};
  transition: background-color 500ms cubic-bezier(0.23, 1, 0.32, 1);

  ${(props) =>
    props.indented &&
    css`
      padding-left: ${16 + 16}px;
      background: ${(props) => props.theme.colors.grey[1]};
      border-color: ${(props) =>
        props.theme.colors.grey[1] && shade(0.9, props.theme.colors.grey[1])};
    `};
`;
MobileMenuItem.defaultProps = {
  is: Link,
  px: [3, 5, 8],
  py: 3,
};

const HeaderInnner = (props) => (
  <Wrapper role="banner">
    <Container>
      <NavSkip
        children="Skip to main content"
        onClick={() => {
          document.getElementById("maincontent").focus();
        }}
      />
      <HeaderContainer align="stretch" justify="space-between">
        <LogoPanel
          to={
            props.windowLang === "en-us"
              ? "/"
              : props.windowLang === "es-mx"
              ? "es-mx"
              : "/ar-eg/"
          }
          onClick={props.closeMenu}
        >
          <LogoContainer open={!props.searchOpen}>
            <Logo />
          </LogoContainer>
          <LogoTitle>
            {props.windowLang === "en-us" ? (
              <span>
                Bank
                <br />
                Information
                <br />
                Center
              </span>
            ) : props.windowLang === "es-mx" ? (
              <span>
                Centro de
                <br />
                Información
                <br />
                Bancaria
              </span>
            ) : (
              <span>
                مركز
                <br />
                معلومات
                <br />
                البنك
              </span>
            )}
          </LogoTitle>
        </LogoPanel>
        <NavContainer align="center" justify="flex-end">
          <Navigation role="navigation" {...props} />
          <Flex align="center" justify="flex-end" mr={[4, 0]}>
            <ButtonWrapper>
              <PrimaryButton
                is={Link}
                to={"/" + props.windowLang + "/" + "support"}
                children={
                  props.windowLang === "en-us"
                    ? "Support us"
                    : props.windowLang === "es-mx"
                    ? "Apóyenos"
                    : "ادعم مركز معلومات البنك"
                }
                onClick={props.closeMenu}
              />
            </ButtonWrapper>
            <LanguageTabletWrapper>
              <Select
                onChange={(event) => {
                  props.changeLang(event.target.value);
                }}
                value={props.currentLang}
                aria-label="Language"
              >
                <option children="Arabic" />
                <option children="English" />
                <option children="Spanish" />
              </Select>
            </LanguageTabletWrapper>
            <Box w={44} mx={3}>
              <Search
                searchOpen={props.searchOpen}
                searchHasHappened={props.searchHasHappened}
                toggleSearch={props.toggleSearch}
                windowLang={props.windowLang}
              />
            </Box>
            <HamburgerMenu onClick={props.toggleMenu} />
          </Flex>
          <LanguageDesktopWrapper>
            <Select
              onChange={(event) => {
                props.changeLang(event.target.value);
              }}
              value={props.currentLang}
              aria-label="Language"
            >
              <option children="Arabic" />
              <option children="English" />
              <option children="Spanish" />
            </Select>
          </LanguageDesktopWrapper>
        </NavContainer>
      </HeaderContainer>
      <LanguageMobileWrapper py={3} px={3} bg="grey.1">
        <Box w="max-content">
          <Select
            onChange={(event) => {
              props.changeLang(event.target.value);
            }}
            value={props.currentLang}
            aria-label="Language"
          >
            <option children="Arabic" />
            <option children="English" />
            <option children="Spanish" />
          </Select>
        </Box>
      </LanguageMobileWrapper>
      {props.menuOpen && (
        <Box mx={[0, -5, -8]}>
          <MobileMenu>
            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "About"
                  : props.windowLang === "es-mx"
                  ? "Sobre BIC"
                  : "عن نحن"
              }
              to={"/" + props.windowLang + "/" + "about"}
              onClick={props.closeMenu}
            />
            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "Team"
                  : props.windowLang === "es-mx"
                  ? "Equipo"
                  : "الفريق"
              }
              to={"/" + props.windowLang + "/" + "team"}
              indented="true"
              onClick={props.closeMenu}
            />
            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "Contact"
                  : props.windowLang === "es-mx"
                  ? "Contacto"
                  : "اتصل"
              }
              to={"/" + props.windowLang + "/" + "contact"}
              indented="true"
              onClick={props.closeMenu}
            />
            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "Support"
                  : props.windowLang === "es-mx"
                  ? "Súmate"
                  : "الدعم"
              }
              to={"/" + props.windowLang + "/" + "support"}
              indented="true"
              onClick={props.closeMenu}
            />
            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "Careers"
                  : props.windowLang === "es-mx"
                  ? "Empleo"
                  : "وظائف"
              }
              to={"/" + props.windowLang + "/" + "careers"}
              indented="true"
              onClick={props.closeMenu}
            />

            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "Reports"
                  : props.windowLang === "es-mx"
                  ? "Informes"
                  : "التقارير"
              }
              to={"/" + props.windowLang + "/" + "reports"}
              indented="true"
              onClick={props.closeMenu}
            />
            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "Campaigns"
                  : props.windowLang === "es-mx"
                  ? "Campañas"
                  : "حملات"
              }
              to={"/" + props.windowLang + "/" + "campaigns"}
              onClick={props.closeMenu}
            />
            {props.data.landing_pages.edges
              .filter((campaign) =>
                campaign.node.data.show_in_navigation === "Show in navigation"
                  ? true
                  : false
              )
              .map((campaign, index) => {
                if (campaign.node.lang === props.windowLang) {
                  return (
                    <MobileMenuItem
                      children={campaign.node.data.title[0].text}
                      indented="true"
                      key={"campaignheader" + index}
                      to={"/" + props.windowLang + "/" + campaign.node.uid}
                      onClick={props.closeMenu}
                    />
                  );
                } else {
                  return null;
                }
              })}
            {props.data.campaigns.edges
              .filter((campaign) =>
                campaign.node.data.show_in_navigation !== "No" ? true : false
              )
              .map((campaign, index) => {
                if (campaign.node.lang === props.windowLang) {
                  return (
                    <MobileMenuItem
                      children={campaign.node.data.title[0].text}
                      indented="true"
                      key={"campaignheader" + index}
                      to={
                        "/" +
                        props.windowLang +
                        "/" +
                        "campaign/" +
                        campaign.node.uid
                      }
                      onClick={props.closeMenu}
                    />
                  );
                } else {
                  return null;
                }
              })}
            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "Regions"
                  : props.windowLang === "es-mx"
                  ? "Regiones"
                  : "مناطق"
              }
              to={"/" + props.windowLang + "/" + "regions"}
              onClick={props.closeMenu}
            />
            {props.data.regions.edges.map((region, index) => {
              if (region.node.lang === props.windowLang) {
                return (
                  <MobileMenuItem
                    children={region.node.data.title[0].text}
                    indented="true"
                    key={"regionheader" + index}
                    to={
                      "/" + props.windowLang + "/" + "region/" + region.node.uid
                    }
                    onClick={props.closeMenu}
                  />
                );
              } else {
                return null;
              }
            })}
            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "Institutions"
                  : props.windowLang === "es-mx"
                  ? "Instituciones"
                  : "مؤسسات"
              }
              to={"/" + props.windowLang + "/" + "institutions"}
              onClick={props.closeMenu}
            />
            {props.data.institutions.edges.map((institution, index) => {
              if (institution.node.lang === props.windowLang) {
                return (
                  <MobileMenuItem
                    children={institution.node.data.title[0].text}
                    indented="true"
                    key={"institutionheader" + index}
                    to={
                      "/" +
                      props.windowLang +
                      "/" +
                      "institution/" +
                      institution.node.uid
                    }
                    onClick={props.closeMenu}
                  />
                );
              } else {
                return null;
              }
            })}
            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "Resources"
                  : props.windowLang === "es-mx"
                  ? "Recursos"
                  : " مصادر"
              }
              to={"/" + props.windowLang + "/" + "resources"}
              onClick={props.closeMenu}
            />
            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "Updates"
                  : props.windowLang === "es-mx"
                  ? "Noticias"
                  : "تحديثات"
              }
              to={"/" + props.windowLang + "/" + "updates"}
              onClick={props.closeMenu}
            />
            <MobileMenuItem
              children={
                props.windowLang === "en-us"
                  ? "Support us"
                  : props.windowLang === "es-mx"
                  ? "Apóyenos"
                  : "ادعم مركز معلومات البنك"
              }
              to={"/" + props.windowLang + "/" + "support"}
              onBlur={props.closeMenu}
            />
          </MobileMenu>
        </Box>
      )}
    </Container>
  </Wrapper>
);

export default class Header extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      menuOpen: false,
      searchOpen: false,
      searchHasHappened: false,
    };
  }

  toggleMenu = () => {
    this.setState({ menuOpen: !this.state.menuOpen });
  };

  openMenu = () => {
    this.setState({ menuOpen: true });
  };

  closeMenu = () => {
    this.setState({ menuOpen: false });
  };

  toggleSearch = () => {
    this.setState({
      searchOpen: !this.state.searchOpen,
      searchHasHappened: true,
    });
  };

  render() {
    return (
      <ThemeProvider
        // theme={
        //   this.props.props.location.pathname === "/ar-eg/" ||
        //   this.props.props.location.pathname === "/es-mx/" ||
        //   this.props.props.location.pathname === "/"
        //     ? theme
        //     : invertTheme
        // }
        theme={theme}
      >
        <HeaderInnner
          {...this.props}
          data={this.props.props.data}
          menuOpen={this.state.menuOpen}
          toggleMenu={this.toggleMenu}
          openMenu={this.openMenu}
          closeMenu={this.closeMenu}
          toggleSearch={this.toggleSearch}
          searchOpen={this.state.searchOpen}
          searchHasHappened={this.state.searchHasHappened}
        />
      </ThemeProvider>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Header.js